import React, { useEffect, useMemo, useState } from 'react'
import { FaPenToSquare } from 'react-icons/fa6'
import styled from 'styled-components'

interface InlineEditableSizingProps {
  $minWidth?: number
}

type InlineEditableState = 'default' | 'warning' | 'error'

interface InlineEditableStateProps {
  $state?: InlineEditableState
}

type InlineEditableStyleProps = InlineEditableSizingProps & InlineEditableStateProps

export const InlineEditableValue = styled.button<InlineEditableStyleProps>`
  border: none;
  border-radius: ${({ theme }) => theme.borderRadius.sm}rem;
  padding: 0;
  margin: 0;
  background: transparent;
  color: ${({ theme }) => theme.colors.neutralBlack};
  text-align: left;
  cursor: pointer;
  font-size: ${({ theme }) => theme.fontSize.md}rem;
  line-height: 1.3;
  min-width: ${({ $minWidth = 150 }) => $minWidth}px;
  display: inline-flex;
  align-items: center;
  justify-content: space-between;
  gap: ${({ theme }) => theme.spacing.xs}rem;
  transition: color 140ms ease-out;

  &:hover .inline-editable-value-text {
    color: ${({ theme }) => theme.colors.metalGray};
  }

  &:hover .inline-edit-icon {
    opacity: 1;
    color: ${({ theme }) => theme.colors.iconGray};
  }

  &:focus-visible {
    outline: 2px solid
      ${({ theme, $state = 'default' }) =>
        $state === 'error' || $state === 'warning' ? theme.colors.warning : theme.colors.iconGray};
    outline-offset: 2px;
    box-shadow: none;
  }

  &:focus-visible .inline-edit-icon {
    opacity: 1;
  }
`

export const InlineEditableText = styled.span.attrs({
  className: 'inline-editable-value-text'
})`
  display: inline;
  transition: color 140ms ease-out;
`

export const InlineEditableInput = styled.input<InlineEditableStyleProps>`
  width: 100%;
  min-width: ${({ $minWidth = 150 }) => $minWidth}px;
  max-width: 100%;
  border: none;
  background: ${({ theme, $state = 'default' }) =>
    $state === 'error'
      ? `color-mix(in srgb, ${theme.colors.warning} 14%, ${theme.colors.neutralWhite})`
      : $state === 'warning'
      ? `color-mix(in srgb, ${theme.colors.warning} 5%, ${theme.colors.neutralWhite})`
      : 'transparent'};
  padding: 0;
  font-size: ${({ theme }) => theme.fontSize.md}rem;
  font-weight: 600;
  line-height: 1.3;
  height: 1.3em;
  color: ${({ theme }) => theme.colors.neutralBlack};
  outline: none;
`

export const InlineEditableEditIcon = styled.span.attrs({
  className: 'inline-edit-icon'
})`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: ${({ theme }) => theme.iconSize.xxs}rem;
  height: ${({ theme }) => theme.iconSize.xxs}rem;
  opacity: 0.35;
  transition: color 140ms ease-out, opacity 140ms ease-out;
  color: ${({ theme }) => theme.colors.metalGray};

  svg {
    width: ${({ theme }) => theme.iconSize.xxs}rem;
    height: ${({ theme }) => theme.iconSize.xxs}rem;
  }
`

type InlineEditableComponentProps = {
  value: string | null | undefined
  onChange: (value: string | null) => void
  editable?: boolean
  inputType?: 'text' | 'date'
  displayValue?: string
  emptyDisplayValue?: string
  state?: InlineEditableState
  title?: string
  minWidth?: number
  ValueComponent?: typeof InlineEditableValue
  InputComponent?: typeof InlineEditableInput
  TextComponent?: typeof InlineEditableText
}

export const InlineEditableField: React.FC<InlineEditableComponentProps> = ({
  value,
  onChange,
  editable = true,
  inputType = 'text',
  displayValue,
  emptyDisplayValue = '-',
  state = 'default',
  title,
  minWidth = 150,
  ValueComponent = InlineEditableValue,
  InputComponent = InlineEditableInput,
  TextComponent = InlineEditableText
}) => {
  const [isEditing, setIsEditing] = useState(false)

  useEffect(() => {
    if (!editable && isEditing) {
      setIsEditing(false)
    }
  }, [editable, isEditing])

  const renderedDisplayValue = useMemo(() => {
    if (displayValue !== undefined && displayValue !== null) return displayValue
    if (value !== undefined && value !== null && value !== '') return value
    return emptyDisplayValue
  }, [displayValue, emptyDisplayValue, value])

  if (isEditing && editable) {
    return (
      <InputComponent
        type={inputType}
        value={value ?? ''}
        $state={state}
        $minWidth={minWidth}
        onChange={event => {
          const nextValue = event.target.value
          onChange(inputType === 'date' ? nextValue || null : nextValue)
        }}
        onBlur={() => setIsEditing(false)}
        autoFocus
      />
    )
  }

  return (
    <ValueComponent
      type="button"
      $state={state}
      $minWidth={minWidth}
      title={editable ? title : undefined}
      onFocus={() => editable && setIsEditing(true)}
      onClick={() => editable && setIsEditing(true)}
    >
      <TextComponent>{renderedDisplayValue}</TextComponent>
      {editable && (
        <InlineEditableEditIcon>
          <FaPenToSquare />
        </InlineEditableEditIcon>
      )}
    </ValueComponent>
  )
}
